<template>
	<div class="animated fadeIn">
		<b-card>
			<b-card-title><i class="icon-people"></i> User</b-card-title>
			<b-card-sub-title>Manages the user registration</b-card-sub-title>
			<b-container fluid class="mt-4">
				<loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />

				<!-- Filter  -->
				<b-row class="mt-2">
					<b-col sm="12" md="3" lg="3">
						<b-button v-b-popover.hover.right="'Toggle to show/hide filter options'" v-b-toggle.collapse-1
							class="filter">
							FILTER OPTIONS
						</b-button>
					</b-col>
					<b-col sm="12">
						<!-- Collapsible Filter Options -->
						<b-collapse id="collapse-1" class="mt-2">
							<b-card>
								<b-row no-gutters>
									<b-col lg="4" md="6" sm="12" class="mr-2">
										<b-form-group label="Company">
											<v-select class="style-chooser" label="text" :options="companyOptions"
												:reduce="(company) => company.value" v-model="filterBy.company">
												<template v-slot:no-options="{ search, searching }">
													<template v-if="searching">
														No results found for
														<em>
															<strong>{{ search }}</strong>
														</em>
													</template>
													<em :style="{ opacity: 0.5 }" v-else>
														Start typing to search for a company
													</em>
												</template>
											</v-select>
										</b-form-group>
									</b-col>
									<b-col lg="4" md="6" sm="12" class="mr-2">
										<b-form-group label="Status">
											<v-select class="style-chooser" label="text" :options="statusOptions"
												:reduce="(isActive) => isActive.value" v-model="filterBy.isActive">
												<template v-slot:no-options="{ search, searching }">
													<template v-if="searching">
														No results found for
														<em>
															<strong>{{ search }}</strong>
														</em>
													</template>
													<em :style="{ opacity: 0.5 }" v-else>
														Start typing to search for status
													</em>
												</template>
											</v-select>
										</b-form-group>
									</b-col>
									<b-col lg="4" md="6" sm="12" class="mr-2">
										<b-form-group label="Type">
											<v-select class="style-chooser" label="text" :options="userTypeOptions"
												:reduce="(userType) => userType" v-model="filterBy.userType">
												<template v-slot:no-options="{ search, searching }">
													<template v-if="searching">
														No results found for
														<em>
															<strong>{{ search }}</strong>
														</em>
													</template>
													<em :style="{ opacity: 0.5 }" v-else>
														Start typing to search for a company
													</em>
												</template>
											</v-select>
										</b-form-group>
									</b-col>
									<b-col lg="4" md="5" sm="12" class="mr-2">
										<b-form-group label="Email">
											<b-form-input name="Email" type="search" v-model="filterBy.emailAddress"
												placeholder />
										</b-form-group>
									</b-col>
									<b-col lg="4" md="5" sm="12" class="mr-2">
										<b-form-group label="Employee No">
											<b-form-input name="Employee No" type="search" v-model="filterBy.employeeNo"
												placeholder />
										</b-form-group>
									</b-col>
								</b-row>

								<b-row no-gutters>
									<b-col sm="12">
										<b-button class="mr-1" variant="success" @click="onFilterRequest">
											Search
										</b-button>
										<b-button class="mr-1" variant="primary" @click="resetFilters">
											Reset
										</b-button>
									</b-col>
								</b-row>
							</b-card>
						</b-collapse>
					</b-col>
				</b-row>

				<!-- Select Actions and Items Per Page Options -->
				<b-row>
					<b-col sm="6" md="3" class="mt-4 mb-2">
						<b-dropdown text=" Select Actions " variant="dark" slot="append">
							<b-dropdown-item v-b-modal.add-user v-show="!isViewer">
								Add User
							</b-dropdown-item>
							<b-dropdown-item>
								<json-excel :data="exportData" :fields="exportFields" type="xls" :name="fileName + '.xls'">
									Export Users in Excel
								</json-excel>
							</b-dropdown-item>
							<b-dropdown-item>
								<json-excel :data="exportData" :fields="exportFieldsCSV" type="csv"
									:name="fileName + '.csv'">
									Export Users to CSV
								</json-excel>
							</b-dropdown-item>
						</b-dropdown>
					</b-col>
					<b-col sm="6" md="4" offset-md="5" class="mt-4 mb-2 text-md-right">
						<b-input-group prepend="Show" append="/ Page">
							<b-form-select :options="pageOptions" v-model="perPage" />
						</b-input-group>
					</b-col>
				</b-row>

				<b-table show-empty striped hover :items="items" :fields="fields" :current-page="currentPage"
					:per-page="perPage" :filter="filter" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
					:sort-direction="sortDirection" responsive>
					<template v-slot:cell(actions)="row">
						<span class="text-nowrap">
							<b-button size="sm" v-b-tooltip.hover.top="'Show/Hide Other Details'" variant="success"
								@click.stop="row.toggleDetails" class="mr-1">
								<i class="fa fa-eye-slash" v-if="row.detailsShowing"></i>
								<i class="fa fa-eye" v-else></i>
							</b-button>
							<b-button size="sm" v-b-modal.edit-user v-b-tooltip.hover.top="'Edit Details'" variant="dark"
								@click.stop="updateSelUser(row.item)" class="mr-1" v-show="!isViewer">
								<i class="fa fa-pencil"></i>
							</b-button>
							<b-button size="sm" v-b-modal.assign-company-access
								v-b-tooltip.hover.top="'Assign Company Access'" variant="primary"
								@click.stop="updateSelUser(row.item)" class="mr-1"
								v-show="!isViewer && hasSiblingCompanies(row.item)">
								<b-icon icon="person-plus-fill"></b-icon>
							</b-button>
						</span>
					</template>

					<template v-slot:cell(status)="row">
						<a @click="updateSelUser(row.item)" v-if="row.item.id != currLoggedUser && !isViewer">
							<b-form-checkbox name="status" v-b-modal.activate-user v-model="row.item.isActive" switch
								size="sm">
								{{ row.item.isActive === 'true' ? 'Active' : 'Inactive' }}
							</b-form-checkbox>
						</a>
						<span v-else>
							{{ row.item.isActive === 'true' ? 'Active' : 'Inactive' }}
						</span>
					</template>

					<template slot="row-details" slot-scope="row">
						<UserDetailsView :row="row" />
					</template>
				</b-table>

				<b-row>
					<b-col md="8" sm="12" class="my-1">
						<span class="totalDisplay">Total: {{ items.length }}</span>
					</b-col>
					<b-col md="4" sm="12" class="my-1">
						<b-pagination align="right" :total-rows="items.length" :per-page="perPage" v-model="currentPage"
							class="my-0" />
					</b-col>
				</b-row>
			</b-container>
		</b-card>

		<!-- Modals here -->
		<AddUser :companyFilter="filterBy.company" :companyOptions="companyOptions" :allCompaniesObj="allCompaniesObj"
			:allUsersObj="allUsersObj" />
		<EditUser :companyOptions="companyOptions" :allCompaniesObj="allCompaniesObj" :allUsersObj="allUsersObj" />
		<ActivateUser :allCompaniesObj="allCompaniesObj" :allUsersObj="allUsersObj" />
		<AssignCompanyAccess :allCompaniesObj="allCompaniesObj" />
	</div>
</template>

<script>
// Component
import AddUser from './user/AddUser';
import EditUser from './user/EditUser';
import ActivateUser from './user/ActivateUser';
import AssignCompanyAccess from './user/AssignCompanyAccess';
import UserDetailsView from './user/UserDetailsView';

// Util
import { DateUtil } from '@/utils/dateutil';
import { DropDownItemsUtil } from '@/utils/dropDownItemsUtil';

// API
import userApi from '@/api/userApi';

// Others
import config from '@/config/env-constants';
import EventBus from '@/shared/event-bus';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import JsonExcel from 'vue-json-excel';
import _ from 'lodash';

export default {
	name: 'user',
	components: {
		AddUser,
		EditUser,
		ActivateUser,
		AssignCompanyAccess,
		UserDetailsView,
		Loading,
		JsonExcel,
	},
	data() {
		return {
			items: [],
			fields: [
				{
					key: 'firstName',
					sortable: true,
				},
				{
					key: 'lastName',
					sortable: true,
				},
				{
					key: 'company',
					sortable: true,
				},
				{
					key: 'type',
					sortable: true,
				},
				'actions',
				'status',
			],
			currentPage: 1,
			perPage: 10,
			totalRows: 0,
			pageOptions: [5, 10, 15, 25, 50, 100],
			sortBy: null,
			sortDesc: false,
			sortDirection: 'asc',
			filter: null,

			companyOptions: [],
			userTypeOptions: config.userTypes,
			statusOptions: config.statusOptionsWithDefault,

			allCompaniesObj: {},
			allUsersObj: {},
			selUser: {},

			defaultFilterBy: {
				company: Object.assign({}, config.companyDefaultValue),
				userType: Object.assign({}, config.userTypeDefaultValue),
				isActive: 'true',
				emailAddress: null,
				employeeNo: null,
			},
			filterBy: {
				company: Object.assign({}, config.companyDefaultValue),
				userType: Object.assign({}, config.userTypeDefaultValue),
				isActive: 'true',
				emailAddress: null,
				employeeNo: null,
			},
			prevFilter: {},

			isSuperAdmin: this.$store.getters.isSuperAdmin,
			isViewer: this.$store.getters.isViewer,
			loggedUser: this.$store.getters.loggedUser,
			loggedUserCompany: this.$store.getters.loggedUserCompany,
			// Check for loader
			isLoading: false,
		};
	},
	watch: {
		'filterBy.employeeNo': function () {
			if (_.isEmpty(this.filterBy.employeeNo)) {
				this.filterBy.employeeNo = null;
			}
		},
		'filterBy.emailAddress': function () {
			if (_.isEmpty(this.filterBy.emailAddress)) {
				this.filterBy.emailAddress = null;
			}
		}
	},
	computed: {
		currLoggedUser() {
			return this.$store.state.auth.email;
		},

		/**
		 * Returns the set of data to be included in the export. For now this just
		 * returns the data as is.
		 *
		 * @returns {Array} the set of data to be included in the export.
		 */
		exportData() {
			return this.items;
		},

		/**
		 * Derives the field information based from the data table configuration.
		 *
		 * @returns {object} the fields to be included in the export.
		 */
		exportFields() {
			return {
				'First Name': 'firstName',
				'Middle Name': 'middleName',
				'Last Name': 'lastName',
				Company: 'company',
				Position: 'position',
				'Employee No': 'employeeNoStr',
				Type: 'type',
				'Contact Number': 'contactNo',
				Email: 'emailAddress',
			};
		},

		exportFieldsCSV() {
			return {
				'First Name': 'firstName',
				'Middle Name': 'middleName',
				'Last Name': 'lastName',
				Company: 'company',
				Position: 'position',
				'Employee No': 'employeeNo',
				Type: 'type',
				'Contact Number': 'contactNo',
				Email: 'emailAddress',
			};
		},

		fileName() {
			let currTimeStamp = DateUtil.getCurrentTimestamp();
			return 'User-' + DateUtil.getDateInDDMMYYYYHHSSFormat(currTimeStamp);
		},
	},
	mounted() {
		setTimeout(() => {
			try {
				// Don't initiate data retrieval when the account is not authenticated
				if (!this.$store.getters.isAuthenticated) {
					return;
				}

				// show loading indicator
				this.isLoading = true;

				this.allCompaniesObj = this.$store.getters.companies;
				this.companyOptions = DropDownItemsUtil.retrieveCompanyItems(
					this.allCompaniesObj, this.loggedUserCompany
				);

				if (!this.isSuperAdmin) {
					this.filterBy.company = DropDownItemsUtil.getCompanyItem(
						this.loggedUserCompany
					);
					this.defaultFilterBy.company = DropDownItemsUtil.getCompanyItem(
						this.loggedUserCompany
					);
				}

				this.allUsersObj = this.$store.getters.users;
				this.filterTableContent();
			} catch (error) {
				this.$toaster.error(
					'Error loading data. Please reload the page again.'
				);
			}

			// hide loading indicator
			this.isLoading = false;
		}, config.timeout);

		// Event Listeners
		EventBus.$on('onCloseAddUser', (userObj) => {
			this.updateTable(userObj);
		});
		EventBus.$on('onCloseEditUser', (userObj) => {
			this.updateTable(userObj);
		});
	},
	methods: {
		updateTable(userObj) {
			if (!_.isEmpty(userObj)) {
				this.allUsersObj[userObj.id] = userObj;
				this.$store.dispatch('updateAllUsers', this.allUsersObj);

				this.filterTableContent();
			}
		},
		filterTableContent() {
			let filteredObj = Object.assign({}, this.allUsersObj);

			_.forEach(this.allUsersObj, (user, userId) => {
				if (
					this.filterBy.company.id !== null &&
					this.filterBy.company.id !== user.companyId
				) {
					delete filteredObj[userId];
				}

				if (this.filterBy.isActive !== null &&
					this.filterBy.isActive !== user.isActive) {
					delete filteredObj[userId];
				}

				if (
					this.filterBy.userType.value !== null &&
					this.filterBy.userType.value !== user.type
				) {
					delete filteredObj[userId];
				}

				if (
					this.filterBy.emailAddress !== null &&
					this.filterBy.emailAddress !== user.emailAddress
				) {
					delete filteredObj[userId];
				}

				if (
					this.filterBy.employeeNo !== null &&
					this.filterBy.employeeNo !== user.employeeNo
				) {
					delete filteredObj[userId];
				}

				user.employeeNoStr = '';
				if (user.employeeNo && user.employeeNo.length > 0) {
					user.employeeNoStr = "'" + user.employeeNo;
				}
			});

			this.items = Object.values(filteredObj);
			this.items = _.sortBy(this.items, ['firstName', 'lastName']);
			this.totalRows = this.items.length;
		},
		onFilterRequest() {
			if (!_.isEqual(this.filterBy, this.prevFilter)) {
				this.retrieveData();
				this.prevFilter = Object.assign({}, this.filterBy);
			}
		},
		resetFilters() {
			if (!_.isEqual(this.filterBy, this.defaultFilterBy)) {
				// reset to default
				this.filterBy = Object.assign({}, this.defaultFilterBy);
				this.retrieveData();
				this.prevFilter = Object.assign({}, this.filterBy);
			}
		},
		async retrieveData() {
			try {
				// show loading indicator
				this.isLoading = true;

				let filter = Object.assign({}, this.filterBy);

				if (this.isSuperAdmin) {
					filter.companyId = this.loggedUserCompany.id;
					const { data } = await userApi.getUsers(
						filter,
						config.view.ADMIN,
						this.loggedUser.id
					);
					this.allUsersObj = _.assign(this.allUsersObj, data.users);
				} else {
					filter.companyId = this.loggedUserCompany.id;
					const { data } = await userApi.getUsers(
						filter,
						config.view.COMPANY,
						this.loggedUser.id
					);
					this.allUsersObj = _.assign(this.allUsersObj, data.users);
				}

				this.filterTableContent();
			} catch (error) {
				this.$toaster.error(
					'Error loading data. Please reload the page again.'
				);
			}

			// hide loading indicator
			this.isLoading = false;
		},
		selectCompany(company) {
			this.filterBy.company = company;
			this.$store.commit('SET_CURR_COMPANY', company);
			this.retrieveData();
		},
		updateSelUser(user) {
			this.selUser = user;
			this.$store.commit('SET_CURR_USER', user);
			EventBus.$emit('onUpdateSelUser', user);
		},
		hasSiblingCompanies(user) {
			let userCompany = this.allCompaniesObj[user.companyId];
			if (!userCompany.hasParentCompany) {
				return _.some(this.allCompaniesObj, { parentCompanyId: user.companyId });
			} else {
				return !_.isEmpty(userCompany.parentCompanyId);
			}
		}
	},
	beforeDestroy() {
		EventBus.$off('onCloseAddUser');
		EventBus.$off('onCloseEditUser');
	},
};
</script>
